// SESSION EXPIRY, APP-WIDE (session-expired-redirect, owner 2026-10-06).
//
// Any backend call answering 401 means the session is gone (expired, or ended
// elsewhere). services/api.ts reports it here; AuthContext listens and sends
// the user to the sign-in screen -- after a warning when a screen still holds
// unsaved work (the same "You have unsaved changes" the tab-close guard uses).
// The URL is left as it is, so signing in again lands on the same page.
//
// ⚠️ NOT ACCESS CONTROL. The server already refused the call; this only
// decides what to render next. 403 is a different answer and never comes here.

type Listener = () => void;

const listeners = new Set<Listener>();
const unsavedChecks = new Set<() => boolean>();

/** Subscribe to "a call came back 401". Returns the unsubscribe. */
export function onSessionExpired(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** Called by services/api.ts on every 401. */
export function notifySessionExpired(): void {
  listeners.forEach((fn) => {
    try { fn(); } catch { /* a listener's failure must not break the caller */ }
  });
}

/** A screen with editable work registers "do I hold unsaved changes?". */
export function registerUnsavedWork(check: () => boolean): () => void {
  unsavedChecks.add(check);
  return () => { unsavedChecks.delete(check); };
}

export function hasUnsavedWork(): boolean {
  for (const check of unsavedChecks) {
    try { if (check()) return true; } catch { /* treat as nothing unsaved */ }
  }
  return false;
}
